import React, { useState, useEffect } from "react";
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider
} from "@mui/material";
import { 
  Description as DescriptionIcon, 
  Add as AddIcon, 
  People as PeopleIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from "@mui/icons-material";

// Import Firebase services
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  onSnapshot,
  where,
  Timestamp,
  doc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { ReportsService } from '../services/firebase';

interface Report {
  id: string;
  reportName: string;
  category?: string;
  patientName: string;
  patientId: string;
  patientEmail?: string;
  patientPhone?: string;
  patientType?: string;
  fileUrls: string[];
  fileNames: string[];
  notes?: string;
  sentToPatient: boolean;
  sentAt?: string;
  createdAt: string;
  updatedAt?: string;
}

interface User {
  id: string;
  name: string;
  phone: string;
  email?: string;
}

const ReportsScreen: React.FC = () => {
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  
  // Data state
  const [reports, setReports] = useState<Report[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [ipOpPatients, setIpOpPatients] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [newReport, setNewReport] = useState({
    reportName: '',
    category: '',
    otherCategory: '',
    patientId: '',
    patientName: '',
    patientEmail: '',
    patientPhone: '',
    notes: '',
    patientType: '' // 'user' or 'ip-op'
  });
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Set up real-time listeners on component mount
  useEffect(() => {
    const unsubscribe = setupRealtimeListeners();
    
    // Cleanup listeners on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const setupRealtimeListeners = () => {
    try {
      console.log('🔄 Setting up real-time listeners for reports...');
      let isFirstLoad = true;
      
      // Real-time listener for reports - only admin-uploaded reports
      const reportsQuery = query(
        collection(db, 'adminReports'),
        orderBy('createdAt', 'desc')
      );
      
      const unsubscribeReports = onSnapshot(reportsQuery, (snapshot) => {
        const reportsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            reportName: data.reportName || 'Report',
            patientName: data.patientName || 'Unknown Patient',
            patientId: data.patientId || '',
            patientEmail: data.patientEmail || '',
            patientPhone: data.patientPhone || '',
            fileUrls: data.fileUrls || [],
            fileNames: data.fileNames || [],
            notes: data.notes || '',
            sentToPatient: data.sentToPatient || false,
            sentAt: data.sentAt || '',
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
          };
        });
        setReports(reportsData);
        
        // Only set loading to false on first load, not on subsequent real-time updates
        if (isFirstLoad) {
          setIsLoadingData(false);
          isFirstLoad = false;
        }
        
        console.log(`🔄 Real-time update: ${reportsData.length} reports`);
      }, (error) => {
        console.error('❌ Error in reports listener:', error);
        setIsLoadingData(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load reports from server', 
          severity: 'error' 
        });
      });

      // Real-time listener for users
      const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().firstName || 'Unknown',
          phone: doc.data().phone || doc.data().mobile || 'N/A',
          email: doc.data().email || ''
        }));
        setUsers(usersData);
        console.log(`🔄 Loaded ${usersData.length} users`);
      }, (error) => {
        console.error('❌ Error in users listener:', error);
      });

      // Load IP/OP patients ONLY from patients collection (single source of truth)
      const unsubscribePatients = onSnapshot(collection(db, 'patients'), (snapshot) => {
        const patientsData: User[] = [];
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          patientsData.push({
            id: doc.id,
            name: data.name || data.fullName || data.firstName || 'Unknown Patient',
            phone: data.phone || data.phoneNumber || data.mobile || 'N/A',
            email: data.email || ''
          });
        });
        
        setIpOpPatients(patientsData);
        console.log(`✅ Loaded ${patientsData.length} IP/OP patients from patients collection`);
        console.log(`📋 Patient IDs:`, patientsData.map(p => ({ id: p.id, name: p.name })));
      }, (error) => {
        console.error('❌ Error in patients listener:', error);
      });

      // Return cleanup function
      return () => {
        console.log('🔌 Cleaning up real-time listeners...');
        unsubscribeReports();
        unsubscribeUsers();
        unsubscribePatients();
      };
    } catch (error) {
      console.error('❌ Error setting up real-time listeners:', error);
      setIsLoadingData(false);
      return () => {};
    }
  };

  // No merge needed - using patients collection as single source of truth

  // Handler functions
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    console.log('📎 Files selected:', files.length);
    console.log('📋 File details:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
    if (files.length > 0) {
      setSelectedFiles(prev => {
        const newFiles = [...prev, ...files];
        console.log('📦 Total files now:', newFiles.length);
        return newFiles;
      });
      setSnackbar({ 
        open: true, 
        message: `${files.length} file(s) selected`, 
        severity: 'success' 
      });
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const selectUser = (user: User) => {
    setNewReport(prev => ({
      ...prev,
      patientId: user.id,
      patientName: user.name,
      patientEmail: user.email || '',
      patientPhone: user.phone
    }));
    setShowUserSelector(false);
    setSearchTerm('');
  };

  const resetForm = () => {
    setNewReport({
      reportName: '',
      category: '',
      otherCategory: '',
      patientId: '',
      patientName: '',
      patientEmail: '',
      patientPhone: '',
      notes: '',
      patientType: ''
    });
    setSelectedFiles([]);
    setLoading(false);
  };

  const handleCreateAndSendReport = async () => {
    console.log('🚀 Starting report creation...');
    console.log('📋 Report data:', newReport);
    console.log('📎 Selected files:', selectedFiles.length);
    console.log('📄 File list:', selectedFiles.map(f => f.name));
    
    if (!newReport.reportName || !newReport.patientId) {
      setSnackbar({ open: true, message: 'Please enter report name and select a user', severity: 'error' });
      return;
    }

    if (!newReport.category) {
      setSnackbar({ open: true, message: 'Please select a report category', severity: 'error' });
      return;
    }

    if (newReport.category === 'Others' && !newReport.otherCategory) {
      setSnackbar({ open: true, message: 'Please specify the category', severity: 'error' });
      return;
    }

    if (!newReport.patientType) {
      setSnackbar({ open: true, message: 'Please select patient type', severity: 'error' });
      return;
    }

    if (selectedFiles.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one file', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      console.log('📝 Creating and sending report...');
      console.log('📋 Report details:', {
        reportName: newReport.reportName,
        category: newReport.category === 'Others' ? newReport.otherCategory : newReport.category,
        patientType: newReport.patientType,
        patientId: newReport.patientId,
        patientName: newReport.patientName,
        filesCount: selectedFiles.length
      });
      
      // Create report using the new admin reports service
      const createdReport = await ReportsService.createAdminReport(
        {
          reportName: newReport.reportName,
          category: newReport.category === 'Others' ? newReport.otherCategory : newReport.category,
          patientType: newReport.patientType,
          patientId: newReport.patientId,
          patientName: newReport.patientName,
          patientEmail: newReport.patientEmail,
          patientPhone: newReport.patientPhone,
          notes: newReport.notes
        },
        selectedFiles
      );

      console.log(`✅ Report created successfully with ID: ${createdReport.id}`);
      console.log('📁 Files uploaded:', createdReport.fileUrls);

      // Reset form and close modal
      resetForm();
      setShowAddModal(false);
      setSnackbar({ 
        open: true, 
        message: `Report "${newReport.reportName}" sent to ${newReport.patientName} successfully!`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('❌ Error creating report:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      setSnackbar({ 
        open: true, 
        message: `Failed to create report: ${error instanceof Error ? error.message : 'Unknown error'}`, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (report: Report) => {
    console.log('👁️ Viewing report:', report);
    console.log('📎 File URLs:', report.fileUrls);
    console.log('📝 File Names:', report.fileNames);
    setSelectedReport(report);
    setShowViewModal(true);
  };

  const handleResendReport = async (report: Report) => {
    try {
      console.log(`📤 Resending report ${report.id} to patient ${report.patientId}...`);
      
      await ReportsService.resendAdminReport(report.id, report.patientId);
      
      setSnackbar({ 
        open: true, 
        message: `Report resent to ${report.patientName} successfully!`, 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('❌ Error resending report:', error);
      setSnackbar({ 
        open: true, 
        message: `Failed to resend report: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  const handleEditReport = (report: Report) => {
    console.log('✏️ Editing report:', report);
    setEditingReport(report);
    setNewReport({
      reportName: report.reportName || '',
      category: report.category || '',
      otherCategory: '',
      patientId: report.patientId || '',
      patientName: report.patientName || '',
      patientEmail: report.patientEmail || '',
      patientPhone: report.patientPhone || '',
      notes: report.notes || '',
      patientType: report.patientType || 'user'
    });
    setShowEditModal(true);
  };

  const handleUpdateReport = async () => {
    if (!editingReport) return;

    if (!newReport.reportName) {
      setSnackbar({ open: true, message: 'Please enter report name', severity: 'error' });
      return;
    }

    if (!newReport.category) {
      setSnackbar({ open: true, message: 'Please select a report category', severity: 'error' });
      return;
    }

    if (newReport.category === 'Others' && !newReport.otherCategory) {
      setSnackbar({ open: true, message: 'Please specify the category', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const reportRef = doc(db, 'adminReports', editingReport.id);
      await updateDoc(reportRef, {
        reportName: newReport.reportName,
        category: newReport.category === 'Others' ? newReport.otherCategory : newReport.category,
        notes: newReport.notes,
        updatedAt: Timestamp.now()
      });

      setSnackbar({ 
        open: true, 
        message: 'Report updated successfully!', 
        severity: 'success' 
      });
      setShowEditModal(false);
      resetForm();
      setEditingReport(null);
    } catch (error: any) {
      console.error('❌ Error updating report:', error);
      setSnackbar({ 
        open: true, 
        message: `Failed to update report: ${error.message}`, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = (report: Report) => {
    if (window.confirm(`Are you sure you want to delete the report "${report.reportName}"? This action cannot be undone.`)) {
      deleteReport(report);
    }
  };

  const deleteReport = async (report: Report) => {
    try {
      console.log(`🗑️ Deleting report ${report.id}...`);
      
      const reportRef = doc(db, 'adminReports', report.id);
      await deleteDoc(reportRef);
      
      setSnackbar({ 
        open: true, 
        message: `Report "${report.reportName}" deleted successfully!`, 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('❌ Error deleting report:', error);
      setSnackbar({ 
        open: true, 
        message: `Failed to delete report: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // Cleanup duplicate reports function
  const handleCleanupDuplicates = async () => {
    if (!window.confirm('This will remove duplicate reports from the medicalReports collection. Reports where type="Medical Report" or type=category will be deleted if they have a proper reportName. Continue?')) {
      return;
    }

    try {
      setIsLoadingData(true);
      console.log('🧹 Starting cleanup of duplicate reports...');

      const medicalReportsRef = collection(db, 'medicalReports');
      const snapshot = await getDocs(medicalReportsRef);
      
      const toDelete: string[] = [];
      const toUpdate: Array<{id: string, reportName: string}> = [];
      
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        const reportName = data.reportName || data.name || data.title;
        
        // Delete if type is generic or equals category (old format)
        // But only if there's a proper reportName to preserve
        if (reportName && reportName !== 'Medical Report') {
          if (data.type === 'Medical Report' || 
              data.type === data.category ||
              !data.type) {
            toDelete.push(docSnap.id);
          }
        } else if (!data.type || data.type === 'Medical Report') {
          // Update reports that have no proper name
          toUpdate.push({ id: docSnap.id, reportName: data.reportName || 'Report' });
        }
      });
      
      console.log(`📊 Found ${toDelete.length} duplicates to delete, ${toUpdate.length} to update`);
      
      // Delete duplicates
      for (const id of toDelete) {
        await deleteDoc(doc(db, 'medicalReports', id));
        console.log(`✅ Deleted duplicate: ${id}`);
      }
      
      // Update reports with missing type field
      for (const item of toUpdate) {
        await updateDoc(doc(db, 'medicalReports', item.id), {
          type: item.reportName,
          name: item.reportName,
          title: item.reportName
        });
        console.log(`✅ Updated: ${item.id}`);
      }
      
      setSnackbar({ 
        open: true, 
        message: `Cleanup complete! Deleted ${toDelete.length} duplicates, updated ${toUpdate.length} reports.`, 
        severity: 'success' 
      });
      
      setIsLoadingData(false);
    } catch (error: any) {
      console.error('❌ Error during cleanup:', error);
      setSnackbar({ 
        open: true, 
        message: `Cleanup failed: ${error.message}`, 
        severity: 'error' 
      });
      setIsLoadingData(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone.includes(searchTerm) ||
    (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Reports Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { 
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' 
            }
          }}
        >
          Upload Report
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Paper sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", borderRadius: 2 }}>
            <DescriptionIcon sx={{ fontSize: 40, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>{reports.length}</Typography>
            <Typography variant="body2">Total Reports</Typography>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Paper sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", color: "white", borderRadius: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 40, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {reports.filter(r => r.sentToPatient).length}
            </Typography>
            <Typography variant="body2">Sent to Users</Typography>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Paper sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", color: "white", borderRadius: 2 }}>
            <PeopleIcon sx={{ fontSize: 40, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              {new Set(reports.map(r => r.patientId)).size}
            </Typography>
            <Typography variant="body2">Unique Users</Typography>
          </Paper>
        </Box>
      </Box>

      {/* Reports Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {isLoadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Report Name</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {report.reportName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.patientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.patientPhone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${report.fileUrls?.length || 0} file(s)`}
                          size="small" 
                          color={report.fileUrls?.length > 0 ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.sentToPatient ? 'Sent' : 'Ready'} 
                          size="small" 
                          color={report.sentToPatient ? 'success' : 'warning'}
                          icon={report.sentToPatient ? <CheckCircleIcon /> : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(report.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewReport(report)}
                            title="View Report"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleEditReport(report)}
                            title="Edit Report"
                          >
                            <EditIcon />
                          </IconButton>
                          {report.sentToPatient && (
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleResendReport(report)}
                              title="Resend to User"
                            >
                              <SendIcon />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteReport(report)}
                            title="Delete Report"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {reports.length === 0 && !isLoadingData && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Reports Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload your first report to send to users
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Upload Report Modal */}
      <Dialog open={showAddModal} onClose={() => { resetForm(); setShowAddModal(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload Report
          <IconButton onClick={() => { resetForm(); setShowAddModal(false); }} sx={{ color: 'error.main' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Report Category */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Report Category *</Typography>
              <TextField
                select
                value={newReport.category}
                onChange={(e) => setNewReport(prev => ({ ...prev, category: e.target.value }))}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Category</option>
                <option value="Blood Tests">Blood Tests</option>
                <option value="Imaging">Imaging</option>
                <option value="Cardiology">Cardiology</option>
                <option value="Pathology">Pathology</option>
                <option value="Radiology">Radiology</option>
                <option value="Urine Tests">Urine Tests</option>
                <option value="Consultation">Consultation</option>
                <option value="Orthopedic">Orthopedic</option>
                <option value="Others">Others</option>
              </TextField>
            </Box>

            {/* Other Category Input - Show only when Others is selected */}
            {newReport.category === 'Others' && (
              <TextField
                label="Specify Category *"
                value={newReport.otherCategory}
                onChange={(e) => setNewReport(prev => ({ ...prev, otherCategory: e.target.value }))}
                placeholder="Enter custom category"
                fullWidth
              />
            )}

            {/* Report Name */}
            <TextField
              label="Report Name *"
              value={newReport.reportName}
              onChange={(e) => setNewReport(prev => ({ ...prev, reportName: e.target.value }))}
              placeholder="e.g., Blood Test Results, X-Ray Report, Medical Certificate"
              fullWidth
            />

            {/* Patient Type Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Patient Type *</Typography>
              <TextField
                select
                value={newReport.patientType}
                onChange={(e) => {
                  setNewReport(prev => ({ 
                    ...prev, 
                    patientType: e.target.value,
                    patientId: '',
                    patientName: '',
                    patientEmail: '',
                    patientPhone: ''
                  }));
                }}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Patient Type</option>
                <option value="user">Registered User</option>
                <option value="ip-op">IP/OP Patient</option>
              </TextField>
            </Box>

            {/* User Selection - Show only if patient type is selected */}
            {newReport.patientType === 'user' && (
              <TextField
                label={`Select User * (${users.length} available)`}
                value={newReport.patientName || ''}
                onClick={() => setShowUserSelector(true)}
                placeholder="Click to select user"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ cursor: 'pointer' }}
              />
            )}

            {/* IP/OP Patient Selection */}
            {newReport.patientType === 'ip-op' && (
              <TextField
                label={`Select IP/OP Patient * (${ipOpPatients.length} available)`}
                value={newReport.patientName || ''}
                onClick={() => setShowUserSelector(true)}
                placeholder="Click to select IP/OP patient"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ cursor: 'pointer' }}
              />
            )}
            {newReport.patientName && (
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography variant="body2" color="primary">
                  <strong>Selected User:</strong> {newReport.patientName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Phone: {newReport.patientPhone}
                </Typography>
              </Box>
            )}

            {/* File Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Upload Files *</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Select one or more files (PDF, Word, Excel, Images, etc.)
              </Typography>
              <input
                accept="*/*"
                hidden
                id="file-upload-new"
                multiple
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="file-upload-new">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ 
                    background: '#4facfe',
                    '&:hover': { background: '#3f9bfe' },
                    py: 1.5
                  }}
                >
                  Choose Files
                </Button>
              </label>
              
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Files ({selectedFiles.length}):
                  </Typography>
                  {selectedFiles.map((file, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => removeFile(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              value={newReport.notes}
              onChange={(e) => setNewReport(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any additional notes or instructions..."
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => { resetForm(); setShowAddModal(false); }}
            variant="outlined"
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateAndSendReport}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ 
              flex: 1,
              background: '#10B981',
              '&:hover': { background: '#059669' }
            }}
          >
            {loading ? 'Sending...' : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Selector Modal */}
      <Dialog open={showUserSelector} onClose={() => { setShowUserSelector(false); setSearchTerm(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {newReport.patientType === 'ip-op' ? 'Select IP/OP Patient' : 'Select User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {(newReport.patientType === 'ip-op' ? 
              ipOpPatients.filter(patient => 
                patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                patient.phone.includes(searchTerm) ||
                (patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
              ) : 
              users.filter(user => 
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone.includes(searchTerm) ||
                (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
              )
            ).map((user) => (
              <React.Fragment key={user.id}>
                <ListItemButton onClick={() => selectUser(user)}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {user.phone}
                        </Typography>
                        {user.email && (
                          <>
                            {' • '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
                <Divider />
              </React.Fragment>
            ))}
            {(newReport.patientType === 'ip-op' ? ipOpPatients : users).filter(u => 
              u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              u.phone.includes(searchTerm) ||
              (u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
            ).length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 
                    `No ${newReport.patientType === 'ip-op' ? 'IP/OP patients' : 'users'} found matching your search` : 
                    `No ${newReport.patientType === 'ip-op' ? 'IP/OP patients' : 'users'} available`
                  }
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowUserSelector(false); setSearchTerm(''); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* View Report Modal */}
      <Dialog open={showViewModal} onClose={() => setShowViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Report Details</Typography>
          <IconButton onClick={() => setShowViewModal(false)} sx={{ color: 'error.main' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReport && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Report Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedReport.reportName}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">User Information</Typography>
                <Typography variant="body1">{selectedReport.patientName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedReport.patientPhone}</Typography>
                {selectedReport.patientEmail && (
                  <Typography variant="body2" color="text.secondary">{selectedReport.patientEmail}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={selectedReport.sentToPatient ? 'Sent to User' : 'Ready to Send'} 
                  color={selectedReport.sentToPatient ? 'success' : 'warning'}
                  icon={selectedReport.sentToPatient ? <CheckCircleIcon /> : undefined}
                  size="small"
                />
                {selectedReport.sentToPatient && selectedReport.sentAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    Sent on: {new Date(selectedReport.sentAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
              {selectedReport.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{selectedReport.notes}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Attached Files ({selectedReport.fileUrls?.length || 0})
                </Typography>
                {selectedReport.fileUrls && selectedReport.fileUrls.length > 0 ? (
                  <Stack spacing={1}>
                    {selectedReport.fileUrls.map((url, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <DescriptionIcon fontSize="small" color="primary" />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {selectedReport.fileNames?.[index] || `File ${index + 1}`}
                        </Typography>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => window.open(url, '_blank')}
                        >
                          Open
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No files attached to this report
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(selectedReport.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)}>Close</Button>
          {selectedReport && selectedReport.sentToPatient && (
            <Button 
              onClick={() => {
                handleResendReport(selectedReport);
                setShowViewModal(false);
              }}
              variant="contained"
              color="info"
              startIcon={<SendIcon />}
            >
              Resend
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Report Modal */}
      <Dialog open={showEditModal} onClose={() => { setShowEditModal(false); resetForm(); setEditingReport(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Report
          <IconButton onClick={() => { setShowEditModal(false); resetForm(); setEditingReport(null); }} sx={{ color: 'error.main' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Report Category */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Report Category *</Typography>
              <TextField
                select
                value={newReport.category}
                onChange={(e) => setNewReport(prev => ({ ...prev, category: e.target.value }))}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select Category</option>
                <option value="Blood Tests">Blood Tests</option>
                <option value="Imaging">Imaging</option>
                <option value="Cardiology">Cardiology</option>
                <option value="Pathology">Pathology</option>
                <option value="Radiology">Radiology</option>
                <option value="Urine Tests">Urine Tests</option>
                <option value="Consultation">Consultation</option>
                <option value="Orthopedic">Orthopedic</option>
                <option value="Others">Others</option>
              </TextField>
            </Box>

            {/* Other Category Input - Show only when Others is selected */}
            {newReport.category === 'Others' && (
              <TextField
                label="Specify Category *"
                value={newReport.otherCategory}
                onChange={(e) => setNewReport(prev => ({ ...prev, otherCategory: e.target.value }))}
                placeholder="Enter custom category"
                fullWidth
              />
            )}

            {/* Report Name */}
            <TextField
              label="Report Name *"
              value={newReport.reportName}
              onChange={(e) => setNewReport(prev => ({ ...prev, reportName: e.target.value }))}
              placeholder="e.g., Blood Test Results, X-Ray Report"
              fullWidth
            />

            {/* Patient Info (Read-only) */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Patient Information</Typography>
              <Typography variant="body1">{newReport.patientName}</Typography>
              <Typography variant="body2" color="text.secondary">{newReport.patientPhone}</Typography>
            </Box>

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              value={newReport.notes}
              onChange={(e) => setNewReport(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any additional notes or instructions..."
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => { setShowEditModal(false); resetForm(); setEditingReport(null); }}
            variant="outlined"
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateReport}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
            sx={{ 
              flex: 1,
              background: '#2563EB',
              '&:hover': { background: '#1D4ED8' }
            }}
          >
            {loading ? 'Updating...' : 'Update Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReportsScreen;